import { useState, type FormEvent } from "react";
import { Button, DataTable, Input, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createShipment, listShipments, updateShipment } from "../../api/shipmentsApi";
import { listSalesExt } from "../../api/salesApi";
import type { ShipmentOut, ShipmentStatus } from "../../api/types";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { canWriteShipments } from "../../roles/permissions";
import { SHIPMENT_STATUS_BADGE_CLASS as STATUS_BADGE_CLASS, SHIPMENT_NEXT_STATUSES as NEXT_STATUSES } from "../../domain/shipmentStatus";

/** Full postal address in one string — every field a `ShipmentOut` carries, not just
 * `city`/`country` (a follow-up call needs the street address too). */
function formatAddress(shipment: ShipmentOut): string {
  const lines = [shipment.address_line1, shipment.address_line2].filter(Boolean);
  const locality = [shipment.city, shipment.region, shipment.postal_code].filter(Boolean).join(", ");
  return [...lines, locality, shipment.country].filter(Boolean).join(", ");
}

/**
 * Shipments — `POST/GET/PATCH /shipments` (§14.1). Create one linked to a Sale (only
 * "MANUAL" carrier is registered server-side — a flat `Tenant.manual_shipping_rate`, no
 * real courier integration), then advance its status along the valid transition pipeline.
 */
export function ShipmentsPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWriteShipments(user.role) : false;
  const shipmentsState = useAsyncData(() => ensureFreshToken().then((token) => listShipments(token)), []);
  const { pageData: shipmentsPageData, pagination: shipmentsPagination, setPage: setShipmentsPage } = usePagination(
    shipmentsState.data ?? [],
    25,
  );
  const salesState = useAsyncData(() => ensureFreshToken().then((token) => listSalesExt(token)), []);

  const [saleId, setSaleId] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [statusDrafts, setStatusDrafts] = useState<Record<string, ShipmentStatus | "">>({});
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!saleId) {
      setFormError("Select a sale to ship.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createShipment(token, {
        sale_id: saleId,
        recipient_name: recipientName,
        contact_email: contactEmail || undefined,
        address_line1: addressLine1,
        address_line2: addressLine2 || undefined,
        city,
        region: region || undefined,
        postal_code: postalCode || undefined,
        country,
        phone: phone || undefined,
        carrier: "MANUAL",
      });
      setSaleId("");
      setRecipientName("");
      setContactEmail("");
      setAddressLine1("");
      setAddressLine2("");
      setCity("");
      setRegion("");
      setPostalCode("");
      setCountry("");
      setPhone("");
      shipmentsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create shipment.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStatusUpdate(shipment: ShipmentOut) {
    const next = statusDrafts[shipment.id];
    if (!next) return;
    setStatusError(null);
    setUpdatingId(shipment.id);
    try {
      const token = await ensureFreshToken();
      await updateShipment(token, shipment.id, { status: next });
      setStatusDrafts((prev) => ({ ...prev, [shipment.id]: "" }));
      shipmentsState.reload();
    } catch (error) {
      setStatusError(error instanceof ApiError ? error.message : "Could not update shipment status — check the transition is valid.");
    } finally {
      setUpdatingId(null);
    }
  }

  const columns: DataTableColumn<ShipmentOut, keyof ShipmentOut>[] = [
    { key: "recipient_name", header: "Recipient", render: (r) => r.recipient_name },
    { key: "sale_id", header: "Sale", render: (r) => (r.sale_id ? <span className="muted">{r.sale_id.slice(0, 8)}</span> : "—") },
    {
      key: "phone",
      header: "Contact",
      render: (r) => (
        <div>
          <div>{r.phone ?? "—"}</div>
          {r.contact_email ? <div className="muted">{r.contact_email}</div> : null}
        </div>
      ),
    },
    { key: "address_line1", header: "Address", render: (r) => formatAddress(r) },
    { key: "carrier", header: "Carrier", render: (r) => r.carrier },
    { key: "rate_amount", header: "Rate", align: "right", render: (r) => r.rate_amount },
    { key: "tracking_number", header: "Tracking", render: (r) => r.tracking_number ?? "—" },
    { key: "status", header: "Status", render: (r) => <span className={`badge ${STATUS_BADGE_CLASS[r.status] ?? "badge--current"}`}>{r.status}</span> },
    {
      key: "id",
      header: "Advance status",
      render: (r) => {
        const options = NEXT_STATUSES[r.status] ?? [];
        if (!canWrite || options.length === 0) return <span className="muted">{options.length === 0 ? "Terminal" : "—"}</span>;
        return (
          <div style={{ display: "flex", gap: 6 }}>
            <select
              className="sl-input"
              style={{ width: 130 }}
              value={statusDrafts[r.id] ?? ""}
              onChange={(e) => setStatusDrafts((prev) => ({ ...prev, [r.id]: e.target.value as ShipmentStatus }))}
            >
              <option value="">Select…</option>
              {options.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <Button type="button" variant="secondary" size="md" disabled={!statusDrafts[r.id] || updatingId === r.id} onClick={() => void handleStatusUpdate(r)}>
              Go
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Shipments</h1>
          <p className="page__subtitle">Create a shipment linked to a sale and advance it through PENDING → PACKED → SHIPPED → DELIVERED (or FAILED).</p>
        </div>
      </div>

      {shipmentsState.error ? <StatusBanner kind="error">{shipmentsState.error}</StatusBanner> : null}
      {statusError ? <StatusBanner kind="error">{statusError}</StatusBanner> : null}
      {!canWrite && user ? (
        <StatusBanner kind="info">Your role ({user.role}) can view shipments. Creating/updating is restricted to OWNER/BRANCH_MANAGER/ECOM_MGR.</StatusBanner>
      ) : null}

      {canWrite ? (
        <div className="panel">
          <h2 className="panel__title">New shipment</h2>
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="ship-sale">
                  Sale
                </label>
                <select id="ship-sale" className="sl-input" value={saleId} onChange={(e) => setSaleId(e.target.value)} disabled={salesState.loading} required>
                  <option value="">Select a sale…</option>
                  {(salesState.data ?? []).map((sale) => (
                    <option key={sale.id} value={sale.id}>
                      {sale.id.slice(0, 8)} — {sale.grand_total}
                    </option>
                  ))}
                </select>
              </div>
              <Input label="Recipient name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} required />
              <Input label="Contact email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
              <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <Input label="Address line 1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} required />
              <Input label="Address line 2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
              <Input label="City" value={city} onChange={(e) => setCity(e.target.value)} required />
              <Input label="Region/State" value={region} onChange={(e) => setRegion(e.target.value)} />
              <Input label="Postal code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
              <Input label="Country" value={country} onChange={(e) => setCountry(e.target.value)} required />
            </div>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Creating…" : "Create shipment"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="panel">
        {shipmentsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={shipmentsPageData}
            getRowId={(row) => row.id}
            pagination={shipmentsPagination}
            onPageChange={setShipmentsPage}
            emptyMessage="No shipments yet."
            caption="Shipments"
          />
        )}
      </div>
    </div>
  );
}
